import {
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { NumberInput } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import { Icon } from "@src/components/ui-kit/Icon";
import { currency, formatDate } from "@src/utils/formatters";
import { fieldNumberModelToInputProps } from "@src/utils/forms/inputHelpers";
import { useStore as useAppStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import type { BudgetItemStore } from "../BudgetItemStore";

type PlannedExpenseMappingModalProps = {
  readonly itemStore: BudgetItemStore;
};

export const PlannedExpenseMappingModal = observer(
  function PlannedExpenseMappingModal({
    itemStore,
  }: PlannedExpenseMappingModalProps) {
    const { workspaceStore } = useAppStore();
    const store = itemStore.plannedExpenseMappingStore;

    return (
      <Modal
        isOpen={store.modalState.isOpen}
        onClose={store.modalState.onClose}
        size="4xl"
        trapFocus={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Expense-to-plan Mapping</Trans>:{" "}
            {currency.formatByWorkspace(store.rowAmountInWorkspace)}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {store.isLoading ? (
              <VStack py="10">
                <Spinner />
              </VStack>
            ) : (
              <>
                <Table>
                  <Thead>
                    <Tr>
                      <Th>
                        <Trans>Contractor</Trans>
                      </Th>
                      <Th>
                        <Trans>Item / Service</Trans>
                      </Th>
                      <Th>
                        <Trans>Date</Trans>
                      </Th>
                      <Th isNumeric>
                        <Trans>Planned buy price</Trans>
                      </Th>
                      <Th />
                      <Th isNumeric>
                        <Trans>Mapped amount</Trans>
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {store.visibleRows.map((row) => (
                      <Tr key={row.key}>
                        <Td>{row.contractorName ?? "-"}</Td>
                        <Td>{row.description ?? "-"}</Td>
                        <Td>{row.date ? formatDate(row.date) : "-"}</Td>
                        <Td isNumeric>
                          {currency.formatByWorkspace(
                            row.remainingBudgetedAmount,
                          )}
                        </Td>
                        <Td>
                          <Tooltip
                            hasArrow
                            label={<Trans>Use remaining</Trans>}
                            placement="top"
                          >
                            <IconButton
                              aria-label={t`Use remaining`}
                              icon={<Icon name="chevron-right" />}
                              onClick={() => {
                                store.quickFill(row.id);
                              }}
                              size="sm"
                              variant="ghost"
                            />
                          </Tooltip>
                        </Td>
                        <Td>
                          <NumberInput
                            currency
                            suffix={workspaceStore.settings?.currency.code}
                            {...fieldNumberModelToInputProps(row.mappedAmount)}
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                {!store.hasVisibleCandidates && (
                  <Text mt="4" color="gray.500">
                    <Trans>
                      No planned expenses available for current filter.
                    </Trans>
                  </Text>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter justifyContent="space-between">
            <Text color={store.remainingAmount === 0 ? "green.500" : "red.500"}>
              <Trans>Remaining</Trans>:{" "}
              {currency.formatByWorkspace(store.remainingAmount)}
            </Text>
            <HStack spacing="2">
              <Button
                colorScheme="grey"
                onClick={store.modalState.onClose}
                variant="ghost"
              >
                <Trans>Cancel</Trans>
              </Button>
              <Button onClick={store.handleSave}>
                <Trans>Save</Trans>
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  },
);
